import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ArrowLeftRight, Plus, Inbox, Search, History, ChevronLeft, ChevronRight, X } from 'lucide-react';
import PageShell from '../../../components/leaves/PageShell';
import { ErrorState } from '../../../components/AsyncStates';
import Button from '../../../components/ui/Button';
import { useToast, Toast } from '../../../hooks/useToast';
// CO-24 — /api/Shift (and its Swap sub-routes) is NOT on the server's RestrictedWriteAllowlist,
// so every swap request/accept/approve genuinely 403s while Restricted/Dormant.
import { useWriteAccess } from '../../../hooks/useWriteAccess';
import RestrictedWriteNotice, { RestrictedWriteHint } from '../../../components/subscription/RestrictedWriteNotice';
import { useShiftSwaps } from '../../../hooks/useShiftSwaps';
import { useShiftSwapHistory } from '../../../hooks/useShiftSwapHistory';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  requestShiftSwap,
  acceptShiftSwap,
  declineShiftSwap,
  cancelShiftSwap,
  approveShiftSwap,
  rejectShiftSwap,
} from '../../../api/Attendance/shiftSwap';
import { ShiftSwapRequestView, ShiftSwapRequestInput, ShiftSwapStatus } from '../../../models/shiftSwap';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import SwapCard, { SwapAction } from './components/SwapCard';
import RequestSwapDialog from './components/RequestSwapDialog';
import RejectSwapDialog from './components/RejectSwapDialog';
import ApproveSwapDialog from './components/ApproveSwapDialog';
import AcceptSwapDialog from './components/AcceptSwapDialog';
import ConfirmSwapDialog from './components/ConfirmSwapDialog';
import { swapStatusLabel } from './components/swapStatus';

// CO-814 A (owner, 2026-09-28): Admin is not HR and no longer decides other employees' swaps, so it
// gets no Approvals tab (the server lists nothing and refuses the decision). Admin keeps the read-only
// Team history tab, which the server still serves to HR and Admin (GetSwapHistory).
const APPROVER_ROLES = ['Manager', 'Hr', 'Chief'];
const TEAM_HISTORY_ROLES = [...APPROVER_ROLES, 'Admin'];
const HISTORY_PAGE = 20;

interface ShiftSwapsProps {
  userId: string;
  company: { id: number } | null;
  role: string;
  isManager: boolean;
  locale: string;
}

type Tab = 'mine' | 'approvals' | 'history';
type MineView = 'active' | 'history';
type StatusFilter = 'all' | ShiftSwapStatus;

interface BusyState {
  id: number;
  action: SwapAction;
}

// Team-history status filters. Empty key = all; 'active' is the server-side pseudo-status
// (Pending + PeerAccepted). The rest are concrete terminal statuses.
const HISTORY_STATUS_FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'active', label: 'In progress' },
  { key: 'Approved', label: 'Approved' },
  { key: 'Rejected', label: 'Rejected' },
  { key: 'Declined', label: 'Declined' },
  { key: 'Cancelled', label: 'Cancelled' },
  { key: 'Expired', label: 'Expired' },
];

const isActiveSwap = (s: ShiftSwapRequestView) =>
  s.status === 'Pending' || s.status === 'PeerAccepted' || s.awaitingMyAcceptance;

// Skeleton placeholders for a list of swap cards — shown instead of a bare spinner so the page
// keeps its shape while loading (product-register: skeletons over centered spinners).
const SwapListSkeleton: React.FC = () => (
  <div className="space-y-3" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <div key={i} className="rounded-xl border border-border bg-card p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="h-4 w-40 animate-pulse rounded bg-muted" />
          <div className="h-5 w-20 animate-pulse rounded-full bg-muted" />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
          <div className="h-20 flex-1 animate-pulse rounded-lg bg-muted" />
          <div className="h-20 flex-1 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    ))}
  </div>
);

const TOAST_COLORS: Record<Toast['type'], string> = {
  success: 'bg-emerald-600',
  error: 'bg-destructive',
  info: 'bg-primary',
  warning: 'bg-amber-600',
};

// Toast presenter for this view. Same a11y semantics as the shared ToastRenderer, but pinned
// above the floating AI-assistant button (the Mowazaf logo mark fixed at the bottom-end corner):
// the shared toast sits at bottom-4 end-4, exactly where the FAB (z-1300) overlaps and clips the
// "Swap request sent" toast. Nudged up + given a higher z so it always reads clearly.
const SwapToast: React.FC<{ toast: Toast | null; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  const { t } = useTranslation();
  if (!toast) return null;
  // Assertive announcements interrupt the screen reader for problems the user must notice
  // (error/warning); polite ones wait their turn (success/info).
  const assertive = toast.type === 'error' || toast.type === 'warning';
  return (
    <div
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      data-testid="app-toast"
      className={`fixed bottom-24 end-4 z-[1301] flex items-center gap-2 rounded-lg px-4 py-3 text-white shadow-lg ${TOAST_COLORS[toast.type]}`}
    >
      <span>{toast.message}</span>
      <button onClick={onDismiss} className="ms-2 hover:opacity-80" aria-label={t('Dismiss')}>
        <X size={16} />
      </button>
    </div>
  );
};

const ShiftSwaps: React.FC<ShiftSwapsProps> = ({ userId, company, role, isManager, locale }) => {
  const { t } = useTranslation();

  // A people-manager's primary Identity role is typically "Clocking"; manager status lives in the
  // isManager flag, so OR it in (mirrors RoleProtectedRoute / useFilteredNavigation's synthetic
  // "Manager" role). The backend remains the security boundary — this gate is UX only.
  const canApprove = APPROVER_ROLES.includes(role) || isManager;
  const canSeeTeamHistory = canApprove || TEAM_HISTORY_ROLES.includes(role);
  const { mySwaps, pendingApprovals, loading, error, refresh, revalidate } = useShiftSwaps(canApprove);

  const [tab, setTab] = useState<Tab>('mine');
  const [mineView, setMineView] = useState<MineView>('active');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [awaitingOnly, setAwaitingOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE);

  // Team-history (manager/HR) — server-paged, only fetched while its tab is open.
  const teamHistory = useShiftSwapHistory(canSeeTeamHistory && tab === 'history');
  // setQuery is a stable useCallback([]) from the hook; capture it so the debounce
  // effect depends on the function, not the whole (per-render) teamHistory object.
  const setTeamQuery = teamHistory.setQuery;
  const [teamSearch, setTeamSearch] = useState('');
  // Debounce the colleague search so we fire one server query after typing stops,
  // not one per keystroke. The primed ref skips the redundant mount push.
  const debouncedTeamSearch = useDebounce(teamSearch, 300);
  const teamSearchPrimed = useRef(false);
  useEffect(() => {
    if (!teamSearchPrimed.current) {
      teamSearchPrimed.current = true;
      return;
    }
    setTeamQuery({ colleague: debouncedTeamSearch.trim() || undefined });
  }, [debouncedTeamSearch, setTeamQuery]);

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<ShiftSwapRequestView | null>(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [approveTarget, setApproveTarget] = useState<ShiftSwapRequestView | null>(null);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState<ShiftSwapRequestView | null>(null);
  const [acceptSubmitting, setAcceptSubmitting] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{ swap: ShiftSwapRequestView; action: 'decline' | 'cancel' } | null>(null);
  const [busy, setBusy] = useState<BusyState | null>(null);

  const { toast, showToast, dismissToast } = useToast();
  const write = useWriteAccess();

  // Keep the hub fresh without a manual reload: an approver watching the queue, or an
  // employee waiting on a colleague, sees new and resolved swaps arrive on their own.
  // Silent revalidation (no skeleton flash) on window focus, tab-visibility, and a gentle
  // interval while the tab is visible.
  useEffect(() => {
    const revalidateIfVisible = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', revalidateIfVisible);
    const interval = window.setInterval(revalidateIfVisible, 60000);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', revalidateIfVisible);
      window.clearInterval(interval);
    };
  }, [revalidate]);

  // Stats double as triage shortcuts: each jumps to (and filters) the list it counts.
  const stats = useMemo(() => {
    const active = mySwaps.filter((s) => s.status === 'Pending' || s.status === 'PeerAccepted').length;
    const awaitingMe = mySwaps.filter((s) => s.awaitingMyAcceptance).length;
    const base: {
      label: string;
      value: number;
      color: string;
      onClick?: () => void;
      ariaLabel?: string;
    }[] = [
      {
        label: t('Active requests'),
        value: active,
        color: 'text-blue-600 dark:text-blue-400',
        onClick: () => { setTab('mine'); setMineView('active'); setStatusFilter('all'); setAwaitingOnly(false); },
      },
      {
        label: t('Awaiting your response'),
        value: awaitingMe,
        color: 'text-amber-600 dark:text-amber-400',
        onClick: () => { setTab('mine'); setMineView('active'); setStatusFilter('all'); setAwaitingOnly(true); },
      },
    ];
    if (canApprove) {
      base.push({
        label: t('To review'),
        value: pendingApprovals.length,
        color: 'text-violet-600 dark:text-violet-400',
        onClick: () => { setTab('approvals'); },
      });
    }
    return base.map((s) => ({ ...s, ariaLabel: `${s.value} ${s.label}` }));
  }, [mySwaps, pendingApprovals.length, canApprove, t]);

  // Don't show a wall of zeros on first run — the empty-state CTA carries that moment instead.
  const showStats = mySwaps.length > 0 || pendingApprovals.length > 0;

  const activeSwaps = useMemo(() => mySwaps.filter(isActiveSwap), [mySwaps]);
  const historySwaps = useMemo(() => mySwaps.filter((s) => !isActiveSwap(s)), [mySwaps]);

  const runAction = async (
    swap: ShiftSwapRequestView,
    action: SwapAction,
    call: () => Promise<{ errors: { description?: string }[] }>,
    successMessage: string,
  ) => {
    setBusy({ id: swap.id, action });
    let succeeded = false;
    try {
      const result = await call();
      if (result.errors.length === 0) {
        succeeded = true;
        showToast(successMessage, 'success');
        await refresh();
      } else {
        showToast(getApiErrorMessage(result, t, t('Something went wrong')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Something went wrong')), 'error');
    }
    setBusy(null);
    return succeeded;
  };

  // Accept and Approve go through their own detail dialogs; Decline and Cancel go through a
  // lightweight confirm so no single misclick reverses a swap for a colleague.
  const handleAccept = (swap: ShiftSwapRequestView) => setAcceptTarget(swap);
  const handleApprove = (swap: ShiftSwapRequestView) => setApproveTarget(swap);
  const handleDecline = (swap: ShiftSwapRequestView) => setConfirmTarget({ swap, action: 'decline' });
  const handleCancel = (swap: ShiftSwapRequestView) => setConfirmTarget({ swap, action: 'cancel' });

  const handleConfirmAction = async () => {
    if (!confirmTarget) return;
    const { swap, action } = confirmTarget;
    const succeeded =
      action === 'decline'
        ? await runAction(swap, 'decline', () => declineShiftSwap(swap.id), t('Swap declined'))
        : await runAction(swap, 'cancel', () => cancelShiftSwap(swap.id), t('Swap request cancelled'));
    if (succeeded) {
      setConfirmTarget(null);
    }
  };

  const handleAcceptConfirm = async (swap: ShiftSwapRequestView) => {
    setAcceptSubmitting(true);
    try {
      const result = await acceptShiftSwap(swap.id);
      if (result.errors.length === 0) {
        showToast(
          swap.autoAppliesOnAccept
            ? t('Swap accepted and applied')
            : t('Swap accepted, awaiting manager approval'),
          'success',
        );
        setAcceptTarget(null);
        await refresh();
      } else {
        showToast(getApiErrorMessage(result, t, t('Something went wrong')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Something went wrong')), 'error');
    }
    setAcceptSubmitting(false);
  };

  const handleApproveConfirm = async (comments: string) => {
    if (!approveTarget) return;
    setApproveSubmitting(true);
    try {
      const result = await approveShiftSwap(approveTarget.id, { comments: comments || null });
      if (result.errors.length === 0) {
        showToast(t('Swap approved'), 'success');
        setApproveTarget(null);
        await refresh();
      } else {
        showToast(getApiErrorMessage(result, t, t('Something went wrong')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Something went wrong')), 'error');
    }
    setApproveSubmitting(false);
  };

  const handleRejectConfirm = async (comments: string) => {
    if (!rejectTarget) return;
    setRejectSubmitting(true);
    try {
      const result = await rejectShiftSwap(rejectTarget.id, { comments: comments || null });
      if (result.errors.length === 0) {
        showToast(t('Swap rejected'), 'success');
        setRejectTarget(null);
        await refresh();
      } else {
        showToast(getApiErrorMessage(result, t, t('Something went wrong')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Something went wrong')), 'error');
    }
    setRejectSubmitting(false);
  };

  const handleRequestSubmit = async (input: ShiftSwapRequestInput) => {
    setRequestSubmitting(true);
    try {
      const result = await requestShiftSwap(input);
      if (result.errors.length === 0) {
        showToast(t('Swap request sent'), 'success');
        setRequestOpen(false);
        setTab('mine');
        setMineView('active');
        setStatusFilter('all');
        setAwaitingOnly(false);
        await refresh();
      } else {
        showToast(getApiErrorMessage(result, t, t('Something went wrong')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Something went wrong')), 'error');
    }
    setRequestSubmitting(false);
  };

  // busyAction passed to a card reflects that card's in-flight action only.
  const cardBusyAction = (id: number): SwapAction | null => (busy && busy.id === id ? busy.action : null);

  const segClass = (active: boolean) =>
    `rounded-md px-3 py-1 text-sm font-medium transition-colors ${
      active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
    }`;

  // Top-level navigation is an underline tablist, visually distinct from the pill segmented control
  // used for the secondary Active/History filter inside "My swaps".
  const topTabClass = (active: boolean) =>
    `relative -mb-px inline-flex items-center gap-2 border-b-2 px-1 pb-3 pt-1 text-sm font-medium transition-colors ${
      active
        ? 'border-primary text-foreground'
        : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
    }`;

  const chipClass = (active: boolean) =>
    `rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? 'border-primary bg-primary/10 text-primary'
        : 'border-border bg-background text-muted-foreground hover:border-primary/50'
    }`;

  const countBadge = (n: number, active: boolean, label?: string) => (
    <span
      aria-label={label}
      className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
        active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
      }`}
    >
      {n}
    </span>
  );

  const renderCard = (swap: ShiftSwapRequestView, mode: 'mine' | 'approval' | 'readonly') => (
    <SwapCard
      key={swap.id}
      swap={swap}
      currentUserId={userId}
      mode={mode}
      locale={locale}
      busyAction={cardBusyAction(swap.id)}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onCancel={handleCancel}
      onApprove={handleApprove}
      onReject={(s) => setRejectTarget(s)}
    />
  );

  const renderApprovals = () => {
    if (pendingApprovals.length === 0) {
      return (
        <div className="flex w-full flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-card py-14 text-center">
          <Inbox size={32} className="text-muted-foreground" />
          <div>
            <p className="text-sm font-semibold text-foreground">{t('Nothing to approve')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('Swap requests from your team will appear here once a colleague accepts.')}
            </p>
          </div>
        </div>
      );
    }
    return <div className="space-y-3">{pendingApprovals.map((s) => renderCard(s, 'approval'))}</div>;
  };

  const base = mineView === 'active' ? activeSwaps : historySwaps;
  const query = search.trim().toLowerCase();
  const otherPartyName = (s: ShiftSwapRequestView) =>
    (s.requesterUserId === userId ? s.counterpartyName : s.requesterName) || '';
  const filteredMine = base.filter(
    (s) =>
      (statusFilter === 'all' || s.status === statusFilter) &&
      (!awaitingOnly || s.awaitingMyAcceptance) &&
      (query === '' || otherPartyName(s).toLowerCase().includes(query)),
  );
  const statusesInView = Array.from(new Set(base.map((s) => s.status)));

  const renderMine = () => {
    // Genuinely no swaps at all — the first-run empty state with a CTA.
    if (mySwaps.length === 0) {
      return (
        <div className="flex w-full flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-card py-14 text-center">
          <ArrowLeftRight size={32} className="text-muted-foreground" />
          <div>
            <p className="text-sm font-semibold text-foreground">{t('No shift swaps yet')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('Request a swap to trade one of your shifts with a colleague.')}
            </p>
          </div>
          <Button variant="default" size="sm" {...write.buttonProps} onClick={() => setRequestOpen(true)}>
            <Plus size={15} className="me-1.5" />
            {t('Request a swap')}
          </Button>
          <RestrictedWriteHint />
        </div>
      );
    }

    let body: React.ReactNode;
    if (base.length === 0) {
      body = (
        <div className="w-full rounded-xl border border-dashed border-border bg-card py-12 text-center">
          <p className="text-sm font-medium text-foreground">
            {mineView === 'active' ? t('No active swaps') : t('No past swaps yet')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {mineView === 'active'
              ? t('Swaps that still need action from you or a colleague show up here.')
              : t('Completed, rejected, and cancelled swaps are kept here.')}
          </p>
        </div>
      );
    } else if (filteredMine.length === 0) {
      body = (
        <div className="w-full rounded-xl border border-dashed border-border bg-card py-12 text-center">
          <p className="text-sm font-medium text-foreground">{t('No swaps match your filters')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('Try a different status or clear the search.')}</p>
        </div>
      );
    } else {
      const visible = mineView === 'history' ? filteredMine.slice(0, historyLimit) : filteredMine;
      body = (
        <div className="space-y-3">
          {visible.map((s) => renderCard(s, 'mine'))}
          {mineView === 'history' && filteredMine.length > historyLimit && (
            <div className="flex justify-center pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHistoryLimit((l) => l + HISTORY_PAGE)}
                data-testid="swap-history-more"
              >
                {t('Show more')}
              </Button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Active / History sub-switch + colleague search */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg bg-muted p-1" role="group" aria-label={t('Swap state')}>
            {(['active', 'history'] as MineView[]).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={mineView === v}
                onClick={() => { setMineView(v); setStatusFilter('all'); setAwaitingOnly(false); setHistoryLimit(HISTORY_PAGE); }}
                data-testid={`swap-mine-${v}`}
                className={`flex items-center gap-1.5 ${segClass(mineView === v)}`}
              >
                {v === 'active' ? t('Active') : t('History')}
                {v === 'active' && activeSwaps.length > 0 && countBadge(activeSwaps.length, mineView === 'active', t('{{count}} active', { count: activeSwaps.length }))}
              </button>
            ))}
          </div>

          <div className="relative w-full max-w-xs">
            <Search size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('Search by colleague')}
              aria-label={t('Search by colleague')}
              data-testid="swap-search"
              className="h-9 w-full rounded-lg border border-border bg-background ps-9 pe-3 text-sm text-foreground placeholder-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
            />
          </div>
        </div>

        {/* Status filter chips (only when the view holds more than one status) */}
        {statusesInView.length > 1 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label={t('Filter by status')}>
            <button type="button" aria-pressed={statusFilter === 'all'} onClick={() => { setStatusFilter('all'); setAwaitingOnly(false); }} className={chipClass(statusFilter === 'all')}>
              {t('All')}
            </button>
            {statusesInView.map((s) => (
              <button key={s} type="button" aria-pressed={statusFilter === s} onClick={() => { setStatusFilter(s); setAwaitingOnly(false); }} className={chipClass(statusFilter === s)}>
                {t(swapStatusLabel(s))}
              </button>
            ))}
          </div>
        )}

        {/* "Awaiting your response" filter, set from the header stat — removable so the user
            can widen back to the full active list. */}
        {awaitingOnly && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              {t('Awaiting your response')}
              <button
                type="button"
                onClick={() => setAwaitingOnly(false)}
                aria-label={t('Clear filter')}
                data-testid="swap-awaiting-clear"
                className="-me-1 rounded-full p-0.5 hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <X size={12} />
              </button>
            </span>
          </div>
        )}

        {body}
      </div>
    );
  };

  const renderTeamHistory = () => {
    const totalPages = Math.max(1, Math.ceil(teamHistory.total / teamHistory.pageSize));
    const activeStatus = teamHistory.query.status ?? '';

    return (
      <div className="space-y-4">
        {/* Colleague search */}
        <div className="relative w-full max-w-xs">
          <Search size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={teamSearch}
            onChange={(e) => setTeamSearch(e.target.value)}
            placeholder={t('Search by colleague')}
            aria-label={t('Search by colleague')}
            data-testid="swap-team-search"
            className="h-9 w-full rounded-lg border border-border bg-background ps-9 pe-3 text-sm text-foreground placeholder-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
          />
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('Filter by status')}>
          {HISTORY_STATUS_FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              aria-pressed={activeStatus === f.key}
              onClick={() => teamHistory.setQuery({ status: f.key || undefined })}
              className={chipClass(activeStatus === f.key)}
            >
              {t(f.label)}
            </button>
          ))}
        </div>

        {teamHistory.loading ? (
          <SwapListSkeleton />
        ) : teamHistory.error ? (
          <div className="py-12">
            <ErrorState message={teamHistory.error} onRetry={teamHistory.refresh} retryText={t('Try again')} title={t('Could not load swap history')} />
          </div>
        ) : teamHistory.items.length === 0 ? (
          <div className="flex w-full flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-card py-14 text-center">
            <History size={32} className="text-muted-foreground" />
            <div>
              <p className="text-sm font-semibold text-foreground">{t('No team swaps found')}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('Swaps involving your team appear here as they are requested and resolved.')}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">{teamHistory.items.map((s) => renderCard(s, 'readonly'))}</div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="text-xs text-muted-foreground">{t('{{count}} total', { count: teamHistory.total })}</p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={teamHistory.page <= 1}
                  onClick={() => teamHistory.setQuery({ page: teamHistory.page - 1 })}
                  aria-label={t('Previous page')}
                  data-testid="swap-history-prev"
                >
                  <ChevronLeft size={15} className="rtl:-scale-x-100" />
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {t('Page {{page}} of {{total}}', { page: teamHistory.page, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={teamHistory.page >= totalPages}
                  onClick={() => teamHistory.setQuery({ page: teamHistory.page + 1 })}
                  aria-label={t('Next page')}
                  data-testid="swap-history-next"
                >
                  <ChevronRight size={15} className="rtl:-scale-x-100" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const topTabs: { id: Tab; label: string; badge?: number }[] = [
    { id: 'mine', label: t('My swaps') },
    ...(canApprove
      ? [{ id: 'approvals' as Tab, label: t('Approvals'), badge: pendingApprovals.length }]
      : []),
    ...(canSeeTeamHistory ? [{ id: 'history' as Tab, label: t('Team history') }] : []),
  ];

  return (
    <PageShell
      title={t('Shift Swaps')}
      description={t('Trade shifts with colleagues, subject to manager approval.')}
      icon={ArrowLeftRight}
      accentColor="blue"
      stats={showStats ? stats : undefined}
      className="min-h-screen bg-background"
    >
      <RestrictedWriteNotice className="mb-4" />

      {/* Toolbar: top-level tabs (underline) + request CTA */}
      <div className="mb-6 border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div role="tablist" aria-label={t('Swap views')} className="flex items-center gap-4 sm:gap-6">
            {topTabs.map((tt) => (
              <button
                key={tt.id}
                type="button"
                role="tab"
                id={`swap-tab-${tt.id}`}
                aria-selected={tab === tt.id}
                aria-controls="swap-panel"
                onClick={() => { setTab(tt.id); setAwaitingOnly(false); }}
                data-testid={`swap-tab-${tt.id}`}
                className={topTabClass(tab === tt.id)}
              >
                {tt.label}
                {tt.badge !== undefined && tt.badge > 0 && countBadge(tt.badge, tab === tt.id, t('{{count}} awaiting approval', { count: tt.badge }))}
              </button>
            ))}
          </div>

          <Button variant="default" size="sm" className="mb-2" {...write.buttonProps} onClick={() => setRequestOpen(true)} data-testid="swap-request-open">
            <Plus size={15} className="me-1.5" />
            {t('Request a swap')}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div id="swap-panel" role="tabpanel" aria-labelledby={`swap-tab-${tab}`} className="w-full">
        {loading && tab !== 'history' ? (
          <SwapListSkeleton />
        ) : error && tab !== 'history' ? (
          <div className="py-12">
            <ErrorState message={error} onRetry={refresh} retryText={t('Try again')} title={t('Could not load shift swaps')} />
          </div>
        ) : tab === 'mine' ? (
          renderMine()
        ) : tab === 'approvals' ? (
          renderApprovals()
        ) : (
          renderTeamHistory()
        )}
      </div>

      {/* Request dialog */}
      <RequestSwapDialog
        open={requestOpen}
        currentUserId={userId}
        companyId={company?.id ?? 0}
        submitting={requestSubmitting}
        locale={locale}
        onClose={() => setRequestOpen(false)}
        onSubmit={handleRequestSubmit}
      />

      {/* Accept confirmation dialog */}
      <AcceptSwapDialog
        swap={acceptTarget}
        submitting={acceptSubmitting}
        locale={locale}
        onClose={() => setAcceptTarget(null)}
        onConfirm={handleAcceptConfirm}
      />

      {/* Approve dialog (optional note) */}
      <ApproveSwapDialog
        swap={approveTarget}
        submitting={approveSubmitting}
        locale={locale}
        onClose={() => setApproveTarget(null)}
        onConfirm={handleApproveConfirm}
      />

      {/* Reject dialog */}
      <RejectSwapDialog
        swap={rejectTarget}
        submitting={rejectSubmitting}
        locale={locale}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleRejectConfirm}
      />

      {/* Decline / Cancel confirmation */}
      <ConfirmSwapDialog
        open={confirmTarget !== null}
        submitting={!!busy && !!confirmTarget && busy.id === confirmTarget.swap.id}
        title={confirmTarget?.action === 'cancel' ? t('Cancel this swap request?') : t('Decline this swap?')}
        description={
          confirmTarget?.action === 'cancel'
            ? t('Your request will be withdrawn and your colleague notified. This cannot be undone.')
            : t('Your colleague will be told you declined. This cannot be undone.')
        }
        confirmLabel={confirmTarget?.action === 'cancel' ? t('Cancel request') : t('Decline swap')}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleConfirmAction}
      />

      <SwapToast toast={toast} onDismiss={dismissToast} />
    </PageShell>
  );
};

interface UserSliceShape {
  userId: string;
  company: { id: number } | null;
  role: string;
  isManager: boolean;
  locale: string;
}

const mapStateToProps = (state: { user: UserSliceShape }) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
  isManager: state.user.isManager,
  locale: state.user.locale,
});

export default connect(mapStateToProps)(ShiftSwaps);
